import { translate } from '@fondationcss/sdk';

// Single source of truth for the MEES frequentation code lists
// (funding source, path type, exemption code). These were previously inline in
// views/planning/frequentation-form.vue; extracted here so the frequentation form
// and the reusable StudentFilter share identical option lists.

export interface CodeOption {
  label: string;
  value: string;
}

/**
 * The FP « motifs de fin de fréquentation » (#1234), in the ministry's hierarchical order: when several
 * apply, the first one listed is the one to declare (Guide de déclaration en formation professionnelle
 * 2024-2025, §2.2.4). Same codes and order as `FrequentationCodes.EndReasons` on the server.
 */
export function getEndReasonOptions(): CodeOption[] {
  return [
    { label: translate('parcours.planning.endReason.12'), value: '12' },
    { label: translate('parcours.planning.endReason.22'), value: '22' },
    { label: translate('parcours.planning.endReason.23'), value: '23' },
    { label: translate('parcours.planning.endReason.05'), value: '05' },
    { label: translate('parcours.planning.endReason.06'), value: '06' },
    { label: translate('parcours.planning.endReason.20'), value: '20' },
    { label: translate('parcours.planning.endReason.09'), value: '09' },
    { label: translate('parcours.planning.endReason.27'), value: '27' },
    { label: translate('parcours.planning.endReason.02'), value: '02' },
    { label: translate('parcours.planning.endReason.04'), value: '04' },
    { label: translate('parcours.planning.endReason.10'), value: '10' },
    { label: translate('parcours.planning.endReason.29'), value: '29' },
    { label: translate('parcours.planning.endReason.32'), value: '32' },
    { label: translate('parcours.planning.endReason.01'), value: '01' },
    { label: translate('parcours.planning.endReason.15'), value: '15' },
    { label: translate('parcours.planning.endReason.31'), value: '31' },
    { label: translate('parcours.planning.endReason.11'), value: '11' },
  ];
}

/** The end of an enrolment as stored, to tell what an edit changes from what it found. */
export interface StoredEnd {
  endDate: string | null;
  endReason: string | null;
}

/**
 * Why the form cannot save this end (#1234), as `FrequentationEndRule` refuses it on the server: an end
 * date goes with its reason. On an existing enrolment the rule only holds once the date or the reason
 * changes, so one ended before the reason existed keeps saving its other fields: `stored` is what the
 * form loaded, null for a creation. Clearing the date clears the reason with it, so a reason alone
 * never reaches this check.
 */
export function endReasonError(endDate: string | null, endReason: string | null, stored: StoredEnd | null): string[] {
  if (!endDate || endReason) return [];
  const unchanged = stored !== null && stored.endDate === endDate && stored.endReason === endReason;
  return unchanged ? [] : [translate('parcours.planning.frequentationForm.endReasonMissing')];
}

/** An enrolment ended without a reason, as before #1234: saved as is, flagged for completion. */
export function isEndReasonToComplete(endDate: string | null, endReason: string | null): boolean {
  return !!endDate && !endReason;
}

export function getFundingSourceOptions(): CodeOption[] {
  return [
    { label: translate('parcours.planning.fundingSource.01'), value: '01' },
    { label: translate('parcours.planning.fundingSource.02'), value: '02' },
    { label: translate('parcours.planning.fundingSource.03'), value: '03' },
    { label: translate('parcours.planning.fundingSource.04'), value: '04' },
    { label: translate('parcours.planning.fundingSource.05'), value: '05' },
    { label: translate('parcours.planning.fundingSource.06'), value: '06' },
    { label: translate('parcours.planning.fundingSource.07'), value: '07' },
    { label: translate('parcours.planning.fundingSource.08'), value: '08' },
    { label: translate('parcours.planning.fundingSource.09'), value: '09' },
    { label: translate('parcours.planning.fundingSource.10'), value: '10' },
  ];
}

export function getPathTypeOptions(): CodeOption[] {
  return [
    { label: translate('parcours.planning.pathType.01'), value: '01' },
    { label: translate('parcours.planning.pathType.02'), value: '02' },
    { label: translate('parcours.planning.pathType.03'), value: '03' },
    { label: translate('parcours.planning.pathType.04'), value: '04' },
    { label: translate('parcours.planning.pathType.05'), value: '05' },
    { label: translate('parcours.planning.pathType.06'), value: '06' },
    { label: translate('parcours.planning.pathType.07'), value: '07' },
    { label: translate('parcours.planning.pathType.08'), value: '08' },
    { label: translate('parcours.planning.pathType.10'), value: '10' },
    { label: translate('parcours.planning.pathType.11'), value: '11' },
    { label: translate('parcours.planning.pathType.12'), value: '12' },
    { label: translate('parcours.planning.pathType.13'), value: '13' },
    { label: translate('parcours.planning.pathType.14'), value: '14' },
    { label: translate('parcours.planning.pathType.17'), value: '17' },
    { label: translate('parcours.planning.pathType.19'), value: '19' },
  ];
}

export function getExemptionCodeOptions(): CodeOption[] {
  return [
    { label: translate('parcours.planning.exemptionCode.11'), value: '11' },
    { label: translate('parcours.planning.exemptionCode.12'), value: '12' },
    { label: translate('parcours.planning.exemptionCode.13'), value: '13' },
    { label: translate('parcours.planning.exemptionCode.14'), value: '14' },
    { label: translate('parcours.planning.exemptionCode.15'), value: '15' },
    { label: translate('parcours.planning.exemptionCode.16'), value: '16' },
    { label: translate('parcours.planning.exemptionCode.17'), value: '17' },
    { label: translate('parcours.planning.exemptionCode.18'), value: '18' },
    { label: translate('parcours.planning.exemptionCode.19'), value: '19' },
    { label: translate('parcours.planning.exemptionCode.20'), value: '20' },
    { label: translate('parcours.planning.exemptionCode.30'), value: '30' },
    { label: translate('parcours.planning.exemptionCode.31'), value: '31' },
    { label: translate('parcours.planning.exemptionCode.32'), value: '32' },
    { label: translate('parcours.planning.exemptionCode.33'), value: '33' },
    { label: translate('parcours.planning.exemptionCode.34'), value: '34' },
    { label: translate('parcours.planning.exemptionCode.35'), value: '35' },
    { label: translate('parcours.planning.exemptionCode.36'), value: '36' },
    { label: translate('parcours.planning.exemptionCode.37'), value: '37' },
    { label: translate('parcours.planning.exemptionCode.38'), value: '38' },
    { label: translate('parcours.planning.exemptionCode.39'), value: '39' },
    { label: translate('parcours.planning.exemptionCode.40'), value: '40' },
    { label: translate('parcours.planning.exemptionCode.42'), value: '42' },
    { label: translate('parcours.planning.exemptionCode.43'), value: '43' },
    { label: translate('parcours.planning.exemptionCode.44'), value: '44' },
    { label: translate('parcours.planning.exemptionCode.45'), value: '45' },
    { label: translate('parcours.planning.exemptionCode.46'), value: '46' },
    { label: translate('parcours.planning.exemptionCode.47'), value: '47' },
    { label: translate('parcours.planning.exemptionCode.48'), value: '48' },
    { label: translate('parcours.planning.exemptionCode.49'), value: '49' },
    { label: translate('parcours.planning.exemptionCode.50'), value: '50' },
    { label: translate('parcours.planning.exemptionCode.51'), value: '51' },
    { label: translate('parcours.planning.exemptionCode.52'), value: '52' },
    { label: translate('parcours.planning.exemptionCode.53'), value: '53' },
    { label: translate('parcours.planning.exemptionCode.54'), value: '54' },
    { label: translate('parcours.planning.exemptionCode.55'), value: '55' },
    { label: translate('parcours.planning.exemptionCode.56'), value: '56' },
    { label: translate('parcours.planning.exemptionCode.61'), value: '61' },
    { label: translate('parcours.planning.exemptionCode.62'), value: '62' },
    { label: translate('parcours.planning.exemptionCode.63'), value: '63' },
    { label: translate('parcours.planning.exemptionCode.70'), value: '70' },
    { label: translate('parcours.planning.exemptionCode.71'), value: '71' },
  ];
}
